---
title: "How to build your first sales pipeline"
description: "Create a Kanban-style sales pipeline in NerdCRM with named stages, win chances, and won and lost stages. Starter templates for phone-heavy teams."
publishedAt: "2026-05-07"
updatedAt: "2026-10-25"
slug: "how-to-build-your-first-pipeline"
---

# How to build your first sales pipeline

A pipeline is a sequence of named stages that a deal moves through. NerdCRM ships a default pipeline; you can edit it, replace it, or run multiple pipelines side-by-side (one per product line, one per region, etc).

Admins and owners build pipelines in **Settings → Pipelines & stages**. The stage editor is a computer screen, so do this part on a laptop or desktop; on a phone the page shows a read-only list.

## Step 1 — Open Pipelines & stages

Go to **Settings → Pipelines & stages**. Each pipeline has a tab at the top, with its number of stages. (**Pipeline** in the sidebar, under **Sell**, opens the board you used last, and the default pipeline the first time. Click the pipeline's name at the top of the board and choose **All pipelines** to land on this same page.)

Click **New pipeline** to start one. To change an existing one, pick its tab, or choose **Edit pipeline** from the name menu on its board.

## Step 2 — Pick a template (or start blank)

The **New pipeline** dialog opens with **Start from** templates:

- **Phone-heavy sales** — Leads → Connected → Demo done → On trial → Completed, plus the lost reasons Not interested and Number off
- **Customer onboarding** — Demo booked → Demo done → Trial → Onboarding → Customer
- **Blank** — just a Won stage and a Lost stage

Templates are a starting point; you can rename, reorder, add, or delete stages after picking one. Click **Create pipeline** and the new pipeline opens as a tab on **Pipelines & stages**.

## Step 3 — Define each stage

Every stage is one row in the stage table. For each, set:

- **Name** — what reps see on the board
- **Type** — **Open**, **Won** or **Lost** (see below)
- **Win chance** — the percentage used for the weighted figure at the top of the board (for example, **On trial** at 70% means we expect 70% of its value to land)

Order is the order of the list: drag the handle at the left of a row to move a stage (or focus the handle and press the up and down arrow keys). The **Deals** column shows how many deals each stage holds. There is no colour to pick. Open stages get darker as deals move forward, won is green and lost is grey. Click **+ Add stage** to add a row; it goes after your last open stage.

The three types:

- **Open** — a deal still in progress. A column on the board.
- **Won** — a deal that closed. Always 100%. Also a column on the board.
- **Lost** — a deal that fell through. Always 0%. Lost stages are not columns: they sit in a **Closed · lost** area at the end of the board, and each one is a **reason**.

Win chance shouldn't go down from one open stage to a later one, and a pipeline needs at least one **Won** stage so deals can be closed as won. If a list breaks either rule, a banner says so. **Fix automatically** only changes win chances: it shows the changes it would make (lost to 0%, won to 100%, an open stage raised to the one before it). A missing **Won** stage is flagged in the banner too, but you add it yourself: click **+ Add stage** and set its type to **Won**. Fixes go into the form first; nothing is saved until you click **Save changes**.

To remove a stage, open the menu on its row and choose **Delete stage**. If the stage still has deals, NerdCRM asks which stage to move them to first, and moves them right away.

## Step 4 — Decide who works it, then save

Below the stage table:

- **Who works this pipeline** — members and read-only people see only the pipelines they are added to. Click **Add** to add someone, or the remove button on their name. Admins and owners always see every pipeline. Each change is saved at once.
- **Pipeline details** — open it for the **Name**, the **Description** and the **Active** switch (switch it off to take a pipeline out of use without deleting it). **Set as default** makes it the pipeline NerdCRM opens first, and where a deal starts when you add it to a pipeline from its own page. It takes effect at once, as does **Delete pipeline** (the default pipeline can't be deleted).

Changes to the stages, name, description and **Active** switch are kept with **Save changes**, in the bar that appears at the bottom of the page. If you change the type of a stage that has deals, NerdCRM asks you to confirm when you save: those deals will be counted as won, lost or open in reports, and it says how many.

## Step 5 — Add deals

Click **New deal** at the top of a board, use **Deals → New deal**, or add one from a contact or company page:

- Pick the contact and company
- Pick the pipeline and the starting stage
- Add the value, and assign an owner (rep)
- Add an expected close date

The deal appears in its stage's column immediately. Drag cards between columns, or open a deal and click a stage on its stage bar. A deal's page also has a **Pipeline** panel that names its pipeline, with **Open board** and **Move to another pipeline**.

## Closing deals

- **Won** — drag the card to a Won column, or open the deal and click **Mark won**.
- **Lost** — drop the card on a reason in **Closed · lost**, or open the deal, click **Mark lost** and pick a **Reason**. The reason is the name of the lost stage, so you can see why deals are lost.

## Multiple pipelines

You can run as many pipelines as you want. Common patterns:

- One pipeline per product line, so the board is clean per product
- One pipeline per region or KAM, when team workflows differ
- A separate **Renewals** pipeline that's distinct from new business

## Tips

- Keep stages concrete and verb-shaped (`Demo done` beats `Engaged`)
- 5–7 open stages is a good range; more than 10 starts to slow reps down
- Do add lost stages. Two or three honest reasons (`Not interested`, `Number off`, `Chose a competitor`) make "why did we lose this?" a single click on **Mark lost**

Need ideas? The [live demo](/demo) ships with a fully populated phone-heavy pipeline you can poke at without signup.
